import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MaskedLines from "../common/MaskedLines";
import DocumentHead from "../common/DocumentHead";
import SmartImage from "../common/SmartImage";
import LiveCover from "../common/LiveCover";
import Lightbox from "./Lightbox";
import VrStage from "./VrStage";
import {
  findNextProject,
  findProjectByRouteParameter,
} from "../../data/projects";
import { studioIdentity } from "../../data/site";
import "./ProjectDetail.css";

/** Anchor for the film section, so the cover can scroll down to it. */
const FILM_SECTION_ID = "film";

/**
 * How a project's gallery is laid out, chosen by the shape of its renders.
 *
 * A gallery of equal tiles makes every image look equally important. Breaking
 * the sequence into "one wide, then two side by side, then two side by side"
 * gives the page a cadence and lets the wide frames act as chapter breaks.
 * That is the `landscape` rhythm, and it is what almost every project uses.
 *
 * Upright renders cannot take that treatment. Frames crop to a fixed aspect
 * ratio, so putting a portrait picture in a 16:9 full-width frame throws away
 * something like half its height — on Aparthotel Jahorina that would cut the
 * top off the building. Those projects pair their renders up in upright
 * frames instead and never get a full-width block.
 */
const GALLERY_RHYTHMS = {
  landscape: {
    blockPattern: [1, 2, 2],
    fullWidthFrame: "frame--cinema",
    pairedFrame: "frame--landscape",
  },
  portrait: {
    blockPattern: [2],
    fullWidthFrame: "frame--portrait",
    pairedFrame: "frame--portrait",
  },
};

/** Used by every project that does not say otherwise. */
const DEFAULT_RENDER_SHAPE = "landscape";

/**
 * Group a project's photographs into the blocks its rhythm calls for.
 *
 * `isFullWidth` comes from the slot in the pattern, not from how many images
 * actually landed in the block. An odd image at the end of a paired rhythm is
 * therefore still laid out as a column — it keeps its place in the grid
 * rather than being promoted to a full-bleed chapter break it was never
 * composed for.
 *
 * @param {string[]} imageUrls
 * @param {number[]} blockPattern Repeating block sizes.
 * @returns {{ imageUrls: string[], isFullWidth: boolean }[]}
 */
function buildGalleryBlocks(imageUrls, blockPattern) {
  const blocks = [];
  let cursor = 0;
  let patternPosition = 0;

  while (cursor < imageUrls.length) {
    const blockSize = blockPattern[patternPosition % blockPattern.length];
    const blockImageUrls = imageUrls.slice(cursor, cursor + blockSize);

    blocks.push({ imageUrls: blockImageUrls, isFullWidth: blockSize === 1 });

    cursor += blockImageUrls.length;
    patternPosition += 1;
  }

  return blocks;
}

/**
 * Whether this project has a photographic sequence to show, as opposed to a
 * single still.
 *
 * `images[0]` is never part of the gallery — it is the picture behind the
 * project title — so a project whose `images` holds one entry has a cover and
 * nothing more. That is the case for a film-led project such as Višnja, where
 * the studio supplied a flythrough and no renders. Those pages drop the
 * gallery, the "Galerija" button and the Vizualizacije count rather than
 * advertising a gallery of one.
 *
 * @param {import("../../data/projects").Project} project
 * @returns {boolean}
 */
function hasPhotographicGallery(project) {
  return project.images.length > 1;
}

/**
 * Metadata rows for the project, omitting anything the archive does not know.
 *
 * @param {import("../../data/projects").Project} project
 * @returns {{ label: string, value: string }[]}
 */
function buildProjectMetadata(project) {
  const rows = [{ label: "Tip objekta", value: project.typology }];

  if (project.location) rows.push({ label: "Lokacija", value: project.location });
  if (project.year) rows.push({ label: "Godina", value: String(project.year) });

  if (hasPhotographicGallery(project)) {
    rows.push({
      label: "Vizualizacije",
      value: String(project.images.length),
    });
  }

  if (project.apartments?.length) {
    rows.push({
      label: "Tlocrti",
      value: `${project.apartments.length} stanova`,
    });
  }

  // Only added where a film exists. Every project has a VR answer, even if it
  // is "no", but a missing animation is not worth a row saying so.
  if (project.film) {
    rows.push({ label: "Animacija", value: "Dostupna" });
  }

  rows.push({
    label: "Virtualna šetnja",
    value: project.hasVR ? "Dostupna" : "Nije dostupna",
  });

  return rows;
}

/**
 * Shown when a URL points at a project that is not in the archive.
 *
 * @returns {JSX.Element}
 */
function ProjectNotFound() {
  return (
    <div className="project-missing">
      <DocumentHead
        title={`Projekat nije pronađen — ${studioIdentity.name}`}
        description="Traženi projekat ne postoji u arhivi."
      />
      <div className="shell project-missing__inner">
        <p className="t-label u-sand">404</p>
        <h1 className="t-headline">Ovaj projekat nije u arhivi.</h1>
        <p className="t-body">
          Moguće je da je veza netačna ili da je projekat uklonjen. Pogledajte
          cijelu arhivu projekata.
        </p>
        <Link className="action" to="/projekti">
          <span>Svi projekti</span>
        </Link>
      </div>
    </div>
  );
}

/**
 * A single project, presented as an editorial document.
 *
 * Reading order: full-bleed cover with the title over it, a metadata table
 * beside the project's own description, the photographic sequence, then the
 * walkthrough film, virtual tour and floorplans where they exist, then a link
 * onward to the next project so the archive can be read end to end without
 * going back.
 *
 * Numeric ids still resolve, so links such as /project/10 shared before the
 * redesign continue to work — see `findProjectByRouteParameter`.
 *
 * @returns {JSX.Element}
 */
function ProjectDetail() {
  const { projectId } = useParams();
  const project = findProjectByRouteParameter(projectId);

  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    startIndex: 0,
    /** "gallery" for photographs, "floorplans" for the drawings. */
    collection: "gallery",
  });
  const [isVirtualTourOpen, setIsVirtualTourOpen] = useState(false);

  /*
    Same cause as the keyed <video> below: this component is not remounted
    when the route moves to another project, so anything held in state here
    carries over.

    The overlays are the case that can actually break. Both trap focus, so
    they cannot normally be left open — but the browser's Back button can
    navigate straight out from under them. Without this, you would land on
    the next project with its lightbox still open at whatever index you had
    reached, which may be past the end of a shorter gallery.
  */
  useEffect(() => {
    setLightboxState({ isOpen: false, startIndex: 0, collection: "gallery" });
    setIsVirtualTourOpen(false);
  }, [projectId]);

  const galleryRhythm =
    GALLERY_RHYTHMS[project?.renderShape ?? DEFAULT_RENDER_SHAPE] ??
    GALLERY_RHYTHMS[DEFAULT_RENDER_SHAPE];

  const galleryBlocks = useMemo(
    () =>
      project
        ? buildGalleryBlocks(
            project.images.slice(1),
            galleryRhythm.blockPattern
          )
        : [],
    [project, galleryRhythm]
  );

  const floorplanImageUrls = useMemo(
    () => project?.apartments?.map((apartment) => apartment.floorplan) ?? [],
    [project]
  );

  if (!project) return <ProjectNotFound />;

  const metadataRows = buildProjectMetadata(project);
  const nextProject = findNextProject(project);
  const showsGallery = hasPhotographicGallery(project);

  /** Takes the visitor from the cover down to the film, as the hero does. */
  const scrollToFilm = () =>
    document
      .getElementById(FILM_SECTION_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  /** @param {number} imageIndex Index within `project.images`. */
  const openGalleryAt = (imageIndex) =>
    setLightboxState({
      isOpen: true,
      startIndex: imageIndex,
      collection: "gallery",
    });

  /** @param {number} floorplanIndex Index within `floorplanImageUrls`. */
  const openFloorplanAt = (floorplanIndex) =>
    setLightboxState({
      isOpen: true,
      startIndex: floorplanIndex,
      collection: "floorplans",
    });

  const closeLightbox = () =>
    setLightboxState((previousState) => ({ ...previousState, isOpen: false }));

  // The gallery blocks render images from index 1 onwards; this converts a
  // position within the flattened block list back to a `project.images` index.
  let flattenedImageCursor = 1;

  return (
    <article className="project">
      <DocumentHead
        title={`${project.name} — ${studioIdentity.name}`}
        description={project.description}
      />

      {/* ------------------------------------------------------------ cover */}
      <header className="project__cover">
        <div className="project__cover-media">
          <LiveCover
            coverSrc={project.cover}
            alt={`${project.name} — glavna vizualizacija`}
            preview={project.preview}
            sizes="100vw"
            priority
          />
          <div className="project__cover-scrim" aria-hidden="true" />
        </div>

        <div className="project__cover-body">
          <div className="project__cover-top shell">
            <Link className="project__back" to="/projekti">
              <span className="project__back-arrow" aria-hidden="true">
                ←
              </span>
              <span>Svi projekti</span>
            </Link>
          </div>

          <div className="project__cover-main shell">
            <p className="project__eyebrow t-label">
              {project.typology}
              {project.location ? ` — ${project.location}` : ""}
            </p>

            <MaskedLines
              as="h1"
              lines={[project.name]}
              className="project__title"
              staggerMs={0}
              initialDelayMs={140}
            />
          </div>

          <div className="project__cover-base">
            <div className="project__cover-base-inner shell">
              {showsGallery && (
                <button
                  type="button"
                  className="project__cover-action"
                  onClick={() => openGalleryAt(0)}
                >
                  <span className="t-label">
                    Galerija — {project.images.length} vizualizacija
                  </span>
                  <span
                    className="project__cover-action-mark"
                    aria-hidden="true"
                  >
                    ↗
                  </span>
                </button>
              )}

              {project.film && (
                <button
                  type="button"
                  className="project__cover-action"
                  data-direction="down"
                  onClick={scrollToFilm}
                >
                  <span className="t-label">Animacija</span>
                  <span
                    className="project__cover-action-mark"
                    aria-hidden="true"
                  >
                    ↓
                  </span>
                </button>
              )}

              {project.hasVR && (
                <button
                  type="button"
                  className="project__cover-action"
                  onClick={() => setIsVirtualTourOpen(true)}
                >
                  <span className="t-label">Virtualna šetnja</span>
                  <span className="project__cover-action-mark" aria-hidden="true">
                    ↗
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------ intro */}
      <section className="project__intro section--tight">
        <div className="shell project__intro-grid">
          <div className="project__intro-copy" data-reveal="rise-sm">
            <p className="t-label project__intro-label">O projektu</p>
            <p className="t-statement project__description">
              {project.description}
            </p>
          </div>

          <dl className="project__meta" data-reveal="rise-sm">
            {metadataRows.map((metadataRow) => (
              <div className="project__meta-row" key={metadataRow.label}>
                <dt className="t-label">{metadataRow.label}</dt>
                <dd className="project__meta-value t-numeral">
                  {metadataRow.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ---------------------------------------------------------- gallery */}
      {galleryBlocks.length > 0 && (
        <section className="project__gallery" aria-label="Galerija projekta">
          {galleryBlocks.map((block, blockIndex) => {
            const { isFullWidth: isFullWidthBlock } = block;
            const blockStartIndex = flattenedImageCursor;
            flattenedImageCursor += block.imageUrls.length;

            return (
              <div
                className="project__gallery-block"
                data-full={isFullWidthBlock ? "true" : "false"}
                // eslint-disable-next-line react/no-array-index-key
                key={blockIndex}
              >
                {block.imageUrls.map((imageUrl, positionInBlock) => {
                  const imageIndex = blockStartIndex + positionInBlock;

                  return (
                    <button
                      type="button"
                      className="project__gallery-item"
                      key={imageUrl}
                      onClick={() => openGalleryAt(imageIndex)}
                      aria-label={`Uvećaj vizualizaciju ${imageIndex + 1} od ${
                        project.images.length
                      }`}
                    >
                      <span
                        className={`frame frame--zoom ${
                          isFullWidthBlock
                            ? galleryRhythm.fullWidthFrame
                            : galleryRhythm.pairedFrame
                        }`}
                        data-reveal="wipe"
                      >
                        <SmartImage
                          src={imageUrl}
                          alt={`${project.name} — vizualizacija ${imageIndex + 1}`}
                          sizes={
                            isFullWidthBlock
                              ? "100vw"
                              : "(min-width: 48rem) 50vw, 100vw"
                          }
                        />
                      </span>
                      <span className="project__gallery-index t-numeral">
                        {String(imageIndex + 1).padStart(2, "0")}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </section>
      )}

      {/* ------------------------------------------------------------- film */}
      {project.film && (
        <section
          className="project__film section"
          id={FILM_SECTION_ID}
          aria-labelledby="film-title"
        >
          <div className="shell">
            <div className="section-head__eyebrow">
              <span className="t-label t-numeral section-head__index">
                FILM
              </span>
              <span className="t-label">Animacija</span>
            </div>

            <h2 id="film-title" className="t-headline project__film-title">
              Šetnja kroz objekat.
            </h2>

            {/*
              The stage is capped at the encode's own width so the film is
              never stretched past the resolution it was rendered at — see the
              note on `resadFilm` in src/data/projects.js.

              `preload="none"` means the page costs nothing until the visitor
              presses play; the poster frame stands in until then. The film is
              muted by default so a click never starts audio unannounced, and
              `controls` lets the visitor turn it back on.
            */}
            <div
              className="project__film-stage"
              style={{
                "--film-width": `${project.film.width}px`,
                "--film-ratio": `${project.film.width} / ${project.film.height}`,
              }}
              data-reveal="rise-sm"
            >
              {/*
                The key is load-bearing. Do not remove it.

                Every project detail page is the same <ProjectDetail /> at the
                same place in the route tree, so moving from one project to
                another changes a URL parameter and nothing else. React keeps
                the existing DOM, including this <video> node.

                That is fine for an <img>, whose `src` can be reassigned. It is
                not fine for a media element: a <video> runs its resource
                selection once, and swapping its <source> children afterwards
                is ignored until something calls load(). So the element went on
                holding the first film the visitor had opened, and every later
                project played that one — Višnja's page, Rešad's film.

                Keying on the slug makes React discard the node and build a new
                one per project, which runs resource selection again.
              */}
              <video
                key={project.slug}
                className="project__film-player"
                poster={project.film.posterUrl}
                width={project.film.width}
                height={project.film.height}
                controls
                loop
                muted
                playsInline
                preload="none"
                controlsList="nodownload"
              >
                {project.film.sources.map((filmSource) => (
                  <source
                    key={filmSource.src}
                    src={filmSource.src}
                    type={filmSource.type}
                  />
                ))}
              </video>
            </div>
          </div>
        </section>
      )}

      {/* --------------------------------------------------------------- VR */}
      {project.hasVR && (
        <section className="project__vr section--ink" aria-labelledby="vr-title">
          <div className="shell project__vr-inner">
            <div className="section-head__eyebrow project__vr-eyebrow">
              <span className="t-label t-numeral section-head__index">VR</span>
              <span className="t-label">Virtualna šetnja</span>
            </div>

            <h2 id="vr-title" className="project__vr-title" data-reveal="rise-sm">
              Prođite kroz objekat prije nego je izgrađen.
            </h2>

            <button
              type="button"
              className="action action--on-ink project__vr-action"
              onClick={() => setIsVirtualTourOpen(true)}
              data-reveal="rise-sm"
            >
              <span>Započni šetnju</span>
            </button>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------- floorplans */}
      {floorplanImageUrls.length > 0 && (
        <section className="project__plans section" aria-labelledby="plans-title">
          <div className="shell">
            <div className="section-head__eyebrow">
              <span className="t-label t-numeral section-head__index">
                {String(floorplanImageUrls.length).padStart(2, "0")}
              </span>
              <span className="t-label">Tlocrti</span>
            </div>

            <h2 id="plans-title" className="t-headline project__plans-title">
              Tlocrti stanova.
            </h2>

            <div className="project__plans-grid">
              {project.apartments.map((apartment, apartmentIndex) => (
                <button
                  type="button"
                  className="plan-card"
                  key={apartment.id}
                  onClick={() => openFloorplanAt(apartmentIndex)}
                  data-reveal="rise-sm"
                >
                  <span className="plan-card__media">
                    <SmartImage
                      src={apartment.floorplan}
                      alt={`Tlocrt — ${apartment.name}`}
                      sizes="(min-width: 64rem) 25vw, (min-width: 48rem) 50vw, 100vw"
                    />
                  </span>
                  <span className="plan-card__caption">
                    <span className="plan-card__name">{apartment.name}</span>
                    <span className="plan-card__mark" aria-hidden="true">
                      ↗
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------- next project */}
      <section className="project__next" aria-label="Sljedeći projekat">
        <Link className="project__next-link" to={`/project/${nextProject.slug}`}>
          <div className="project__next-media" aria-hidden="true">
            <SmartImage src={nextProject.cover} alt="" sizes="100vw" />
          </div>

          <div className="project__next-body shell">
            <p className="t-label project__next-label">Sljedeći projekat</p>
            <p className="project__next-title">{nextProject.name}</p>
            <p className="t-meta project__next-meta">
              {[nextProject.typology, nextProject.location]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </Link>
      </section>

      {/* --------------------------------------------------------- overlays */}
      <Lightbox
        isOpen={lightboxState.isOpen}
        images={
          lightboxState.collection === "floorplans"
            ? floorplanImageUrls
            : project.images
        }
        startIndex={lightboxState.startIndex}
        projectName={
          lightboxState.collection === "floorplans"
            ? `${project.name} — tlocrti`
            : project.name
        }
        onClose={closeLightbox}
      />

      {project.hasVR && (
        <VrStage
          isOpen={isVirtualTourOpen}
          onClose={() => setIsVirtualTourOpen(false)}
          tourUrl={project.vrLink}
          projectName={project.name}
        />
      )}
    </article>
  );
}

export default ProjectDetail;
